import { BRAND_SECONDARY } from "../../../utils/Styles";
import {
  Callout,
  DatePicker,
  DayOfWeek,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  DirectionalHint,
  Fabric,
  IColumn,
  IDetailsListProps,
  IDetailsRowStyles,
  PrimaryButton,
  SelectionMode,
  Stack,
  Text,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import * as React from "react";
import { getMorTempAndRhReadings } from "../../../services/infrastructure-automation/InfrastructureAutmationService";
import { Loading } from "../../../services/Loading";
import { ToastContext } from "../../common/ToastProvider";
import moment from "moment";
import {
  IMorTempRhReadings,
  ITempRhReadings,
} from "../../../vm";
import { childrenGap, childrenGap8 } from "../../common/Constants";
import NoDataTile from "../../common/NoDataTile";
import { getSLAMatrixConfig } from "../../../services/sla-matrix/SlaMatrixService";
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiDotsVertical,
} from "@mdi/js";
import Icon from "@mdi/react";
import ManageTempAndRhDialog from "./ManageTempAndRhDialog";
import { toFixedValue } from "../../../services/UtilService";
import { updateTempAndHumidity } from "../../../services/customers/CustomerService";

export interface AmbientTempAndRhMainProps {
  datacenterId: string
};

var slaMatrix: any = {};

const AmbientTempAndRhMain: React.FC<AmbientTempAndRhMainProps> = ({ datacenterId }) => {
  // hooks
  const { showToast } = React.useContext(ToastContext);

  // state
  const [isLoading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    tempRhObj: {
      date: new Date(),
      temp: 0,
      rh: 0,
      sensorId: "",
    },
    manageActionsCallout: {
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
    },
    manageTempRhDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    isOnEdit: false,
  } as {
    tempRhObj: ITempRhReadings;
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: IMorTempRhReadings;
    };
    manageTempRhDialog: {
      isOpen: boolean;
      index?: number;
      data?: IMorTempRhReadings;
    };
    isOnEdit: boolean;
  });
  const [search, setSearch] = React.useState({
    datacenterId: datacenterId,
    isAmbient: true,
    startDate: moment().startOf("day").toDate(),
    endDate: moment().startOf("day").toDate(),
  } as {
    datacenterId: string;
    isAmbient: boolean;
    startDate: Date | string;
    endDate: Date | string;
  });
  const [readings, setReadings] = React.useState([] as IMorTempRhReadings[]);

  // local hook
  React.useLayoutEffect(() => {
    const asyncFunc = async () => {
      setLoading(true);
      // await searchMetaDataForSearch();
      await searchMorReadings();
    };
    asyncFunc();
  }, []);

  const searchMetaDataForSearch = async () => {
    // sla matrix
    let slaResult = await getSLAMatrixConfig("");
    if (slaResult?.success) {
      slaMatrix = slaResult.data;
    } else {
      showToast(slaResult?.message || "Error while getting sla matrix config", "error");
    }
  };

  const searchMorReadings = async () => {
    setLoading(true);
    const searchObj = { ...search };
    searchObj.startDate = moment(searchObj.startDate).format("YYYY-MM-DD");
    searchObj.endDate = moment(searchObj.endDate).format("YYYY-MM-DD");

    const result = await getMorTempAndRhReadings(searchObj);
    if (result && result.success) {
      setReadings(result.data);
    } else {
      showToast(
        result.message || "Error while fetching ambient sensors readings",
        "error"
      );
    }
    setLoading(false);
  };

  const onSearch = () => {
    searchMorReadings();
  };

  const isTempNotInThreshold = (tempValue: number) => {
    if (!slaMatrix.temperature) return false;
    if (
      tempValue < slaMatrix.temperature.min ||
      tempValue > slaMatrix.temperature.max
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isRhNotInThreshold = (rhValue: number) => {
    if (!slaMatrix.humidity) return false;
    if (
      rhValue < slaMatrix.humidity.min ||
      rhValue > slaMatrix.humidity.max
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleDialogOpen = (
    isOpen: boolean,
    index?: number,
  ) => {
    let data = undefined;
    if (index !== undefined) {
      data = { ...readings[index] };
    }
    setState({
      ...state,
      manageTempRhDialog: {
        isOpen,
        index,
        data,
      },
    });
  };

  const handleDialogClose = () => {
    setState({
      ...state,
      manageTempRhDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: IMorTempRhReadings
  ) => {
    setState({
      ...state,
      isOnEdit: false,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      },
    });
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "actions":
        return (
          <>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <Stack horizontal tokens={childrenGap}>
                <React.Fragment>
                  <TooltipHost
                    content={`Save`}
                    id={`saveTempRh`}
                    calloutProps={{ gapSpace: 0 }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <span onClick={onUpdateTempRh}>
                      <Icon
                        path={mdiCheckCircleOutline}
                        size={0.8}
                        className="pointer"
                        color="green"
                      />
                    </span>
                  </TooltipHost>
                  <TooltipHost
                    content={`Cancel`}
                    id={`cancelTempRh`}
                    calloutProps={{ gapSpace: 0 }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <span
                      onClick={() =>
                        setState({
                          ...state,
                          isOnEdit: false,
                          tempRhObj: {
                            ...state.tempRhObj,
                            date: new Date(),
                            temp: 0,
                            rh: 0,
                            sensorId: "",
                          },
                          manageActionsCallout: {
                            isOpen: false,
                            index: undefined,
                            element: undefined,
                            data: undefined,
                          },
                        })
                      }
                    >
                      <Icon
                        path={mdiCloseCircleOutline}
                        size={0.8}
                        className="pointer"
                        color="red"
                      />
                    </span>
                  </TooltipHost>
                </React.Fragment>
              </Stack>
            ) : (
              <span
                onClick={(ele) =>
                  handleCallOutOpenClose(true, ele, index, item)
                }
              >
                <Icon
                  path={mdiDotsVertical}
                  size={0.9}
                  className="pointer"
                  color={BRAND_SECONDARY}
                />
              </span>
            )}
          </>
        );
      case "temp":
        return (
          <React.Fragment>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <TextField
                value={String(state.tempRhObj.temp)}
                type="number"
                name="temp"
                onChange={handleChange}
              // className="width-80"
              />
            ) : (
              <React.Fragment>
                <span
                  className={
                    item.temp !== "NA" && isTempNotInThreshold(item.temp)
                      ? "text-danger"
                      : ""
                  }
                >
                  {toFixedValue(item.temp)}
                </span>
                {item.temp !== "NA" && isTempNotInThreshold(item.temp) && (
                  <Icon
                    path={
                      item.temp < slaMatrix.temperature.min
                        ? mdiArrowDown
                        : mdiArrowUp
                    }
                    size={0.5}
                    color="#D62828"
                    style={{ marginBottom: -2 }}
                  />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        );
      case "rh":
        return (
          <React.Fragment>
            {state.isOnEdit && state.manageActionsCallout.index === index ? (
              <TextField
                value={String(state.tempRhObj.rh)}
                type="number"
                name="rh"
                onChange={handleChange}
              // className="width-80"
              />
            ) : (
              <React.Fragment>
                <span
                  className={
                    item.rh !== "NA" && isRhNotInThreshold(item.rh)
                      ? "text-danger"
                      : ""
                  }
                >
                  {toFixedValue(item.rh)}
                </span>
                {item.rh !== "NA" && isRhNotInThreshold(item.rh) && (
                  <Icon
                    path={
                      item.rh < slaMatrix.humidity.min
                        ? mdiArrowDown
                        : mdiArrowUp
                    }
                    size={0.5}
                    color="#D62828"
                    style={{ marginBottom: -2 }}
                  />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        );
      case "sensorName":
        return (
          <Text styles={{ root: { fontWeight: 600 } }}>
            {item[column.fieldName as any]}
          </Text>
        );
      default:
        return <span>{column.fieldName ? item[column.fieldName] : ""}</span>;
    }
  };

  const handleChange = (e: any) => {
    setState({
      ...state,
      tempRhObj: {
        ...state.tempRhObj,
        [e.target.name]: e.target.value,
      },
    });
  };

  const onUpdateTempRh = async () => {
    let tempRhObj = { ...state.tempRhObj };
    tempRhObj.temp = Number(tempRhObj.temp);
    tempRhObj.rh = Number(tempRhObj.rh);

    if (!state.tempRhObj.temp) {
      tempRhObj.temp = 0;
    }
    if (!state.tempRhObj.rh) {
      tempRhObj.rh = 0;
    }
    setLoading(true);
    let result = await updateTempAndHumidity(tempRhObj);
    if (result?.success) {
      let allReadings = [...readings];
      if (state.manageActionsCallout.index !== undefined) {
        allReadings[state.manageActionsCallout.index].temp =
          state.tempRhObj.temp || 0;
        allReadings[state.manageActionsCallout.index].rh =
          state.tempRhObj.rh || 0;
      }
      setState({
        ...state,
        tempRhObj: {
          ...state.tempRhObj,
          date: new Date(),
          temp: 0,
          rh: 0,
          sensorId: "",
        },
        isOnEdit: false,
        manageActionsCallout: {
          isOpen: false,
          index: undefined,
          element: undefined,
          data: undefined,
        },
      });
      showToast("Ambient Temp & Rh value updated successfully", "success");
    } else {
      showToast(result?.message || "Error while updating ambient temp & rh", "error");
    }
    setLoading(false);
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <Stack horizontal horizontalAlign="start" className="pt-16">
        <Stack.Item>
          <Stack horizontal wrap tokens={childrenGap}>
            <Stack.Item>
              <DatePicker
                firstDayOfWeek={DayOfWeek.Sunday}
                showMonthPickerAsOverlay={true}
                value={new Date(search.startDate)}
                showGoToToday={false}
                onSelectDate={(date: Date | null | undefined) => {
                  setSearch({
                    ...search,
                    startDate: date as Date,
                    endDate: date as Date,
                  });
                }}
                placeholder="Select a date..."
                ariaLabel="Select a date"
                styles={{ root: { width: 200 } }}
              />
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton text="Search" onClick={() => onSearch()} />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
      <br />
      <Stack>
        <Stack.Item className="max-table-width">
          {readings && readings.length > 0 ? (
            <Fabric>
              <DetailsList
                items={readings}
                columns={[
                  {
                    key: "sensorName",
                    name: "Sensor Name",
                    fieldName: "sensorName",
                    minWidth: 200,
                    maxWidth: 250,
                    isResizable: true,
                  },
                  {
                    key: "type",
                    name: "Sensor Type",
                    fieldName: "type",
                    minWidth: 100,
                    maxWidth: 150,
                    isResizable: true,
                  },
                  {
                    key: "ip",
                    name: "IP",
                    fieldName: "ip",
                    minWidth: 100,
                    maxWidth: 150,
                    isResizable: true,
                  },
                  {
                    key: "tempObjectId",
                    name: "Temp Object Id",
                    fieldName: "tempObjectId",
                    minWidth: 100,
                    maxWidth: 150,
                    isResizable: true,
                  },
                  {
                    key: "rhObjectId",
                    name: "RH Object Id",
                    fieldName: "rhObjectId",
                    minWidth: 100,
                    maxWidth: 150,
                    isResizable: true,
                  },
                  {
                    key: "temp",
                    name: "Temperature",
                    fieldName: "temp",
                    minWidth: 150,
                    maxWidth: 200,
                    isResizable: true,
                  },
                  {
                    key: "rh",
                    name: "Humidity",
                    fieldName: "rh",
                    minWidth: 150,
                    maxWidth: 200,
                    isResizable: true,
                  },
                  {
                    key: "actions",
                    name: "Actions",
                    fieldName: "actions",
                    minWidth: 150,
                    maxWidth: 200,
                    isResizable: true,
                  },
                ]}
                compact={true}
                setKey="set"
                onRenderItemColumn={_renderItemColumn as any}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                onRenderRow={_onRenderRow}
              />
            </Fabric>
          ) : (
            <NoDataTile title="No Ambient Sensor Found"></NoDataTile>
          )}
        </Stack.Item>
      </Stack>
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout &&
            state.manageActionsCallout.data?.sensorId
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
        // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  handleDialogOpen(true, state.manageActionsCallout.index);
                }}
                className="callout-options"
              >
                View
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  setState({
                    ...state,
                    tempRhObj: {
                      ...state.tempRhObj,
                      date: search.startDate,
                      temp: state.manageActionsCallout.data?.temp as number,
                      rh: state.manageActionsCallout.data?.rh as number,
                      sensorId: state.manageActionsCallout.data?.sensorId,
                    },
                    manageActionsCallout: {
                      ...state.manageActionsCallout,
                      isOpen: false,
                    },
                    isOnEdit: true,
                  });
                }}
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageTempRhDialog.isOpen && (
        <ManageTempAndRhDialog
          handleDialogClose={() => handleDialogClose()}
          data={state.manageTempRhDialog.data as IMorTempRhReadings}
          date={search.startDate}
          slaMatrix={slaMatrix}
        />
      )}
    </React.Fragment>
  );
};

export default AmbientTempAndRhMain;
